import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import axios from "axios";
import {
  Building2, RefreshCw, Plus, FileUp, Check, X, AlertTriangle,
  ArrowRightLeft, Calendar, FileText, CheckCircle2, Clock, Search, Edit2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const ConciliacionBancaria = () => {
  const [loading, setLoading] = useState(true);
  const [cuentasBanco, setCuentasBanco] = useState([]);
  const [conciliaciones, setConciliaciones] = useState([]);
  const [activeTab, setActiveTab] = useState("conciliaciones");
  
  const [modalNueva, setModalNueva] = useState(false);
  const [modalConciliar, setModalConciliar] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conciliacionActualId, setConciliacionActualId] = useState(null);
  const [importandoCSV, setImportandoCSV] = useState(false);
  
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState("");
  const [movimientosSistema, setMovimientosSistema] = useState([]);
  const [movimientosSeleccionados, setMovimientosSeleccionados] = useState([]);
  
  const [formNueva, setFormNueva] = useState({
    cuenta_tesoreria_id: "",
    periodo_desde: "",
    periodo_hasta: new Date().toISOString().split('T')[0],
    saldo_banco: "",
    observaciones: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cuentasRes, conciliacionesRes] = await Promise.all([
        axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/cuentas-tesoreria"),
        axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/conciliaciones")
      ]);
      
      setCuentasBanco(cuentasRes.data.filter(c => c.tipo === "BANCO"));
      setConciliaciones(conciliacionesRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMovimientosPendientes = async (cuentaId, fechaDesde, fechaHasta) => {
    try {
      let url = API_URL + "/api/erp/empresas/" + TENANT_ID + "/conciliacion/" + cuentaId + "/movimientos-pendientes";
      if (fechaDesde) url += "?fecha_desde=" + fechaDesde;
      if (fechaHasta) url += (fechaDesde ? "&" : "?") + "fecha_hasta=" + fechaHasta;
      
      const response = await axios.get(url);
      setMovimientosSistema(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleOpenNueva = () => {
    const primeraCuenta = cuentasBanco.length > 0 ? cuentasBanco[0].id.toString() : "";
    setFormNueva({
      cuenta_tesoreria_id: primeraCuenta,
      periodo_desde: "",
      periodo_hasta: new Date().toISOString().split('T')[0],
      saldo_banco: "",
      observaciones: ""
    });
    setModalNueva(true);
  };

  const handleImportarCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      toast.error("Solo se aceptan archivos CSV o TXT");
      return;
    }
    // Ask for cuenta
    const cuentaId = window.prompt("Ingrese ID de cuenta de tesorería (1=Caja, 2=Banco):", "2");
    if (!cuentaId) return;
    
    setImportandoCSV(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(
        API_URL + "/api/erp/empresas/" + TENANT_ID + "/extractos-bancarios/importar-csv?cuenta_tesoreria_id=" + cuentaId,
        formData, { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error importando CSV");
    }
    setImportandoCSV(false);
    e.target.value = "";
  };

  const handleCrearConciliacion = async () => {
    if (!formNueva.cuenta_tesoreria_id || !formNueva.periodo_desde || !formNueva.saldo_banco) {
      toast.error("Complete los campos requeridos");
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(API_URL + "/api/erp/empresas/" + TENANT_ID + "/conciliaciones", {
        cuenta_tesoreria_id: parseInt(formNueva.cuenta_tesoreria_id),
        periodo_desde: formNueva.periodo_desde,
        periodo_hasta: formNueva.periodo_hasta,
        saldo_banco: parseFloat(formNueva.saldo_banco),
        observaciones: formNueva.observaciones
      });
      
      toast.success("Conciliación creada - Diferencia: " + new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(response.data.diferencia));
      setModalNueva(false);
      setConciliacionActualId(response.data.id);
      fetchData();
      
      // Abrir modal de conciliación
      setCuentaSeleccionada(formNueva.cuenta_tesoreria_id);
      await fetchMovimientosPendientes(formNueva.cuenta_tesoreria_id, formNueva.periodo_desde, formNueva.periodo_hasta);
      setMovimientosSeleccionados([]);
      setModalConciliar(true);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear conciliación");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMovimiento = (movId) => {
    setMovimientosSeleccionados(prev => 
      prev.includes(movId) 
        ? prev.filter(id => id !== movId)
        : [...prev, movId]
    );
  };

  const formatCurrency = (value) => {
    return "$ " + new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleConfirmarConciliacion = async (concId) => {
    if (!window.confirm("¿Confirmar esta conciliacion? No podra modificarse despues.")) return;
    try {
      await axios.put(API_URL + "/api/erp/empresas/" + TENANT_ID + "/conciliaciones/" + concId + "/confirmar");
      toast.success("Conciliacion confirmada");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al confirmar");
    }
  };

  const handleAnularConciliacion = async (concId) => {
    if (!window.confirm("¿Anular esta conciliacion? Se revertiran los movimientos conciliados.")) return;
    try {
      await axios.put(API_URL + "/api/erp/empresas/" + TENANT_ID + "/conciliaciones/" + concId + "/anular");
      toast.success("Conciliacion anulada");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al anular");
    }
  };

  const handleEditarConciliacion = async (conc) => {
    setConciliacionActualId(conc.id);
    setCuentaSeleccionada(conc.cuenta_tesoreria_id.toString());
    await fetchMovimientosPendientes(conc.cuenta_tesoreria_id, conc.periodo_desde, conc.periodo_hasta);
    setMovimientosSeleccionados([]);
    setModalConciliar(true);
  };

  const getEstadoBadge = (estado) => {
    switch(estado) {
      case "CONFIRMADO":
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Confirmado</Badge>;
      case "BORRADOR":
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Borrador</Badge>;
      case "ANULADO":
        return <Badge className="bg-red-100 text-red-700"><X className="w-3 h-3 mr-1" />Anulado</Badge>;
      default:
        return <Badge variant="outline">{estado}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="conciliacion-bancaria">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Conciliación Bancaria</h1>
          <p className="text-gray-600">Concilie los movimientos bancarios con el sistema</p>
        </div>
        <div className="flex gap-2">
          <label className="cursor-pointer inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 transition-colors" data-testid="btn-importar-csv">
            <Upload className="w-4 h-4 mr-2" />
            {importandoCSV ? "Importando..." : "Importar CSV"}
            <input type="file" accept=".csv,.txt" onChange={handleImportarCSV} className="hidden" disabled={importandoCSV} />
          </label>
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handleOpenNueva} className="bg-[#2853B9] hover:bg-[#1E3876]">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Conciliación
          </Button>
        </div>
      </div>

      {/* Resumen de cuentas bancarias */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cuentasBanco.map((cuenta) => {
          const ultimaConciliacion = conciliaciones.find(c => c.cuenta_tesoreria_id === cuenta.id);
          return (
            <Card key={cuenta.id} className="cao-card">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{cuenta.nombre}</CardTitle>
                    <CardDescription>{cuenta.banco_nombre || cuenta.codigo}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Saldo Sistema:</span>
                    <span className="font-bold">{formatCurrency(cuenta.saldo_actual)}</span>
                  </div>
                  {ultimaConciliacion && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Última conciliación:</span>
                        <span>{ultimaConciliacion.fecha_conciliacion}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Diferencia:</span>
                        <span className={ultimaConciliacion.diferencia !== 0 ? "text-red-600 font-bold" : "text-green-600"}>
                          {formatCurrency(ultimaConciliacion.diferencia)}
                        </span>
                      </div>
                    </>
                  )}
                  {!ultimaConciliacion && (
                    <p className="text-xs text-gray-400 italic">Sin conciliaciones previas</p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {cuentasBanco.length === 0 && (
          <Card className="cao-card col-span-3">
            <CardContent className="p-8 text-center text-gray-500">
              No hay cuentas bancarias configuradas. Cree una cuenta de tipo "Banco" en Tesorería.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="conciliaciones">
            <FileText className="w-4 h-4 mr-2" />Conciliaciones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="conciliaciones" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <CardTitle>Historial de Conciliaciones</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Saldo Banco</TableHead>
                    <TableHead className="text-right">Saldo Sistema</TableHead>
                    <TableHead className="text-right">Diferencia</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conciliaciones.map((conc) => (
                    <TableRow key={conc.id}>
                      <TableCell>{conc.fecha_conciliacion}</TableCell>
                      <TableCell>{conc.cuenta_nombre}</TableCell>
                      <TableCell className="text-sm">
                        {conc.periodo_desde} al {conc.periodo_hasta}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(conc.saldo_banco)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(conc.saldo_sistema)}</TableCell>
                      <TableCell className={"text-right font-mono font-bold " + (conc.diferencia !== 0 ? "text-red-600" : "text-green-600")}>
                        {formatCurrency(conc.diferencia)}
                      </TableCell>
                      <TableCell>{getEstadoBadge(conc.estado)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {conc.estado === "BORRADOR" && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-800 h-8 px-2"
                                onClick={() => handleEditarConciliacion(conc)}
                                title="Editar/Conciliar movimientos"
                                data-testid={"btn-editar-conc-" + conc.id}
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-800 h-8 px-2"
                                onClick={() => handleConfirmarConciliacion(conc.id)}
                                title="Confirmar conciliacion"
                                data-testid={"btn-confirmar-conc-" + conc.id}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Confirmar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 h-8 px-2"
                                onClick={() => handleAnularConciliacion(conc.id)}
                                title="Anular conciliacion"
                                data-testid={"btn-anular-conc-" + conc.id}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Anular
                              </Button>
                            </>
                          )}
                          {conc.estado === "CONFIRMADO" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 h-8 px-2"
                              onClick={() => handleAnularConciliacion(conc.id)}
                              title="Anular conciliacion"
                              data-testid={"btn-anular-conc-" + conc.id}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Anular
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {conciliaciones.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No hay conciliaciones registradas
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Nueva Conciliación */}
      <Dialog open={modalNueva} onOpenChange={setModalNueva}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Nueva Conciliación Bancaria</DialogTitle>
            <DialogDescription>
              Ingrese los datos del extracto bancario para conciliar
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Cuenta Bancaria *</Label>
              <Select 
                value={formNueva.cuenta_tesoreria_id} 
                onValueChange={(v) => setFormNueva({...formNueva, cuenta_tesoreria_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cuenta" />
                </SelectTrigger>
                <SelectContent>
                  {cuentasBanco.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.nombre} - {c.banco_nombre || c.codigo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Período Desde *</Label>
                <Input 
                  type="date" 
                  value={formNueva.periodo_desde}
                  onChange={(e) => setFormNueva({...formNueva, periodo_desde: e.target.value})}
                />
              </div>
              <div>
                <Label>Período Hasta *</Label>
                <Input 
                  type="date" 
                  value={formNueva.periodo_hasta}
                  onChange={(e) => setFormNueva({...formNueva, periodo_hasta: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Saldo según Extracto Bancario *</Label>
              <Input 
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formNueva.saldo_banco}
                onChange={(e) => setFormNueva({...formNueva, saldo_banco: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">Saldo final que figura en el extracto del banco</p>
            </div>
            
            <div>
              <Label>Observaciones</Label>
              <Textarea 
                value={formNueva.observaciones}
                onChange={(e) => setFormNueva({...formNueva, observaciones: e.target.value})}
                placeholder="Notas adicionales..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalNueva(false)}>Cancelar</Button>
            <Button 
              onClick={handleCrearConciliacion} 
              disabled={saving}
              className="bg-[#2853B9] hover:bg-[#1E3876]"
            >
              {saving ? "Creando..." : "Crear y Conciliar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Conciliar Movimientos */}
      <Dialog open={modalConciliar} onOpenChange={setModalConciliar}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Conciliar Movimientos</DialogTitle>
            <DialogDescription>
              Seleccione los movimientos del sistema que coinciden con el extracto bancario
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                <span className="font-medium">Movimientos del Sistema</span>
              </div>
              <Badge variant="outline">
                {movimientosSeleccionados.length} seleccionados
              </Badge>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Concepto</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movimientosSistema.map((mov) => (
                  <TableRow 
                    key={mov.id} 
                    className={movimientosSeleccionados.includes(mov.id) ? "bg-green-50" : ""}
                  >
                    <TableCell>
                      <Checkbox
                        checked={movimientosSeleccionados.includes(mov.id) || mov.conciliado}
                        disabled={mov.conciliado}
                        onCheckedChange={() => handleToggleMovimiento(mov.id)}
                      />
                    </TableCell>
                    <TableCell>{mov.fecha}</TableCell>
                    <TableCell>
                      <Badge className={mov.tipo === "INGRESO" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                        {mov.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell>{mov.concepto}</TableCell>
                    <TableCell className="font-mono text-sm">{mov.referencia || "-"}</TableCell>
                    <TableCell className={"text-right font-mono " + (mov.tipo === "INGRESO" ? "text-green-600" : "text-red-600")}>
                      {mov.tipo === "EGRESO" && "-"}{formatCurrency(mov.importe)}
                    </TableCell>
                    <TableCell>
                      {mov.conciliado ? (
                        <Badge className="bg-green-100 text-green-700">
                          <Check className="w-3 h-3 mr-1" />Conciliado
                        </Badge>
                      ) : (
                        <Badge variant="outline">Pendiente</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {movimientosSistema.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No hay movimientos en el período seleccionado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Resumen */}
            <div className="p-4 bg-gray-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Total movimientos seleccionados:</span>
                <span className="font-bold">
                  {formatCurrency(
                    movimientosSistema
                      .filter(m => movimientosSeleccionados.includes(m.id))
                      .reduce((sum, m) => sum + (m.tipo === "INGRESO" ? m.importe : -m.importe), 0)
                  )}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalConciliar(false)}>Cerrar</Button>
            <Button 
              onClick={async () => {
                if (!conciliacionActualId) { toast.error("Error: no hay conciliación activa"); return; }
                setSaving(true);
                try {
                  const items = movimientosSeleccionados.map(movId => ({ movimiento_id: movId }));
                  await axios.post(API_URL + "/api/erp/empresas/" + TENANT_ID + "/conciliaciones/" + conciliacionActualId + "/conciliar", items);
                  toast.success("Movimientos conciliados correctamente");
                  setModalConciliar(false);
                  fetchData();
                } catch(e) { toast.error(e.response?.data?.detail || "Error al conciliar"); }
                setSaving(false);
              }}
              className="bg-[#2853B9] hover:bg-[#1E3876]"
              disabled={movimientosSeleccionados.length === 0 || saving}
            >
              <Check className="w-4 h-4 mr-2" />
              {saving ? "Guardando..." : "Guardar Conciliación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConciliacionBancaria;
